import { omit, pick } from '@styled-system/props';
import CloseOutlinedIcon from '@uala/icons/CloseOutlinedIcon';
import React, { useRef } from 'react';

import mergeRefs from '../utils/mergeRefs';
import { ComponentProps } from '../utils/types';
import { CloseIconButton, Input, SearchIconButton, Wrapper } from './styles';

export type CollapsibleInputPropsInterface =
  React.InputHTMLAttributes<HTMLInputElement> & {
    isOpen?: boolean;
    icon: React.ReactNode;
    closeButton?: boolean;
    onOpen?: () => void;
    onClose?: () => void;
  };

const CollapsibleInput = React.forwardRef<
  HTMLInputElement,
  CollapsibleInputPropsInterface
>(
  (
    {
      isOpen = false,
      disabled,
      icon,
      closeButton = true,
      onClose,
      onOpen,
      ...rest
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>();

    const focusInput = () => {
      if (onOpen) onOpen();
      if (inputRef.current) inputRef.current.focus();
    };

    const blurInput = () => {
      if (onClose) onClose();
      if (inputRef.current) inputRef.current.blur();
    };

    const wrapperProps = pick(rest);
    const inputProps = omit(rest);

    return (
      <Wrapper className={isOpen ? 'focused' : ''} {...wrapperProps}>
        <Input
          aria-label="search-input"
          type="text"
          disabled={disabled}
          {...inputProps}
          ref={mergeRefs([inputRef, ref])}
        />
        <SearchIconButton
          id="search-filter-button"
          role="button"
          size="icon"
          variant="unfilled"
          onClick={focusInput}
          disabled={disabled}
        >
          {icon}
        </SearchIconButton>
        {closeButton && (
          <CloseIconButton
            id="close-filter-button"
            role="button"
            size="icon"
            variant="unfilled"
            onClick={blurInput}
          >
            <CloseOutlinedIcon id="close-filter-icon" />
          </CloseIconButton>
        )}
      </Wrapper>
    );
  }
);

export type CollapsibleInputProps = ComponentProps<typeof CollapsibleInput>;
export default CollapsibleInput;
